import { FlexBox, LoadingButton } from '@airship-platform/jam-web/components';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { SectionWrapper } from '../../../patterns';

const DownPaymentFormSchema = z.object({
  appointmentProposalDownPayment: z.string(),
});

type DownPaymentFormInputs = z.infer<typeof DownPaymentFormSchema>;

const sanitizeDollarInput = (value: string): string | null => {
  if (!/^\d*\.?\d*$/.test(value)) return null;
  return value.replace(/^0+(?=\d)/, '');
};

const DownPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentProposal, updateAppointmentProposal, isLoading } =
    useAppointmentProposals();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<DownPaymentFormInputs>({
    resolver: zodResolver(DownPaymentFormSchema),
    defaultValues: {
      appointmentProposalDownPayment:
        currentProposal?.downPayment?.amount?.toString() ?? '0',
    },
  });

  if (!currentProposal) {
    return;
  }

  const onSubmit = async (data: DownPaymentFormInputs) => {
    const globalAmount = +data.appointmentProposalDownPayment || 0;

    await updateAppointmentProposal(
      currentProposal.appointmentId,
      currentProposal.appointmentProposalId,
      { downPayment: { amount: globalAmount, type: 'FIXED' as const } }
    );

    navigate(-1);
  };

  return (
    <SectionWrapper
      maxWidth={'48rem'}
      width={'100%'}
      header="Down payment"
      subheader="Set a down payment to reduce the financed balance."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../financing',
          label: 'Cancel',
        },
        right: {
          component: LoadingButton,
          label: 'Save',
          loading: isLoading,
          disabled: !isDirty || isLoading,
          onClick: handleSubmit(onSubmit),
        },
      }}
    >
      <FlexBox flexDirection={'column'} gap={3} width={'100%'}>
        <Controller
          name="appointmentProposalDownPayment"
          control={control}
          render={({ field: { onChange, ...field } }) => (
            <TextField
              {...field}
              onChange={(e) => {
                const sanitized = sanitizeDollarInput(e.target.value);
                if (sanitized !== null) onChange(sanitized);
              }}
              label="Proposal down payment"
              type="text"
              placeholder="0.00"
              slotProps={{
                input: {
                  inputMode: 'decimal',
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="textPrimary">
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ flex: 1 }}
            />
          )}
        />
      </FlexBox>
    </SectionWrapper>
  );
};

export default DownPaymentPage;
